import React, { useEffect, useState } from "react";
import { Button, MenuItem, Select } from "@mui/material";
import { toast } from "react-toastify";
import { useStore } from "../../helpers";
import { parseEnquiryHrRecord, saveEnquiryHrToApi } from "./enquiry-hr-api";
import { EnquiryHrComments } from "./EnquiryHrComments";
import type { EnquiryHrRecord } from "./enquiry-hr-types";
import { ENQUIRY_HR_STATUS_OPTIONS } from "./enquiry-settings";
import { getEnquiryId, type EnquiryRow } from "./enquiry-helpers";
import { safe_get } from "../../report-visuals";

type EnquiryHrPanelProps = {
    row: EnquiryRow;
    refreshTable: () => void;
};

const TAG_INPUT_PLACEHOLDER = "Type and press Enter";
const TAG_LABEL = "Add tags";

export const EnquiryHrPanel = ({ row, refreshTable }: EnquiryHrPanelProps) => {
    const rootStore = useStore();
    const enquiryId = getEnquiryId(row);
    const [record, setRecord] = useState<EnquiryHrRecord>(() => parseEnquiryHrRecord(row));
    const [selectedStatus, setSelectedStatus] = useState(record.status);
    const [tags, setTags] = useState<string[]>([]);
    const [tagInputValue, setTagInputValue] = useState("");
    const [savingStatus, setSavingStatus] = useState(false);

    useEffect(() => {
        // console.log(row, "row here");

        const currentRowTags =
            (safe_get(row, "comments", "") as string).length > 0
                ? (safe_get(row, "comments", "") as string).split(",")
                : "";
        console.log(currentRowTags, "tags here");
        const parsedRecord = parseEnquiryHrRecord(row);
        setRecord(parsedRecord);
        setSelectedStatus(parsedRecord.status);
        setTags([...currentRowTags]);
        setTagInputValue("");
    }, [row]);

    if (enquiryId == null) {
        return (
            <section className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                HR status and comments
            </section>
        );
    }

    const handleSubmit = async () => {
        if (!rootStore.api) return;

        const nextStatus = selectedStatus.trim();
        const pendingTag = tagInputValue.trim();
        const nextTags = pendingTag && !tags.includes(pendingTag) ? [...tags, pendingTag] : tags;
        const normalizedTags = nextTags.map((tag) => tag.trim()).filter(Boolean);
        const hasChanges = nextStatus !== record.status || normalizedTags.length > 0;

        if (!hasChanges) return;

        setSavingStatus(true);
        const response = await saveEnquiryHrToApi(rootStore.api, enquiryId, {
            status: nextStatus || record.status,
            comment: normalizedTags.length ? normalizedTags.join(",") : undefined,
        });
        if (!response.ok) {
            toast.error("Could not save HR status and tags. Please try again.");
            setSavingStatus(false);
            return;
        }

        setRecord((current) => ({ ...current, status: nextStatus || current.status }));
        setTags(nextTags);
        setTagInputValue("");
        setSavingStatus(false);
        setTimeout(() => refreshTable(), 1000);
    };

    const handleAddTag = () => {
        const trimmed = tagInputValue.trim();
        if (trimmed && !tags.includes(trimmed)) {
            setTags((current) => [...current, trimmed]);
        }
        setTagInputValue("");
    };

    const handleRemoveTag = (tagToRemove: string) => {
        setTags((current) => current.filter((tag) => tag !== tagToRemove));
    };

    const handleTagInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTagInputValue(event.target.value);
    };

    const handleTagInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter" || event.key === ",") {
            event.preventDefault();
            handleAddTag();
        }
        if (event.key === "Backspace" && !tagInputValue && tags.length > 0) {
            event.preventDefault();
            handleRemoveTag(tags[tags.length - 1]);
        }
    };

    return (
        <section className="rounded-xl border border-gray-200 bg-white p-4">
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-violet-700">HR</h3>
            <div className="mb-4">
                <p className="mb-1.5 text-xs font-medium text-gray-500">Status</p>
                <Select
                    value={selectedStatus}
                    onChange={(event) => setSelectedStatus(String(event.target.value))}
                    size="small"
                    disabled={savingStatus}
                    fullWidth
                    sx={{ borderRadius: "8px", backgroundColor: "white" }}
                >
                    {ENQUIRY_HR_STATUS_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
            </div>
            <div>
                <p className="mb-1.5 text-xs font-medium text-gray-500">{TAG_LABEL}</p>
                <div className="flex min-h-[56px] flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2">
                    {tags.map((tag, index) => (
                        <div
                            key={`${tag}-${index}`}
                            className="flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium"
                            style={{
                                backgroundColor: "#ede9fe",
                                color: "#1f2937",
                            }}
                        >
                            {tag}
                            <button
                                type="button"
                                onClick={() => handleRemoveTag(tag)}
                                className="flex h-4 w-4 items-center justify-center rounded-full bg-black/20 text-xs font-bold transition hover:bg-black/40"
                            >
                                ×
                            </button>
                        </div>
                    ))}
                    <input
                        type="text"
                        value={tagInputValue}
                        onChange={handleTagInputChange}
                        onKeyDown={handleTagInputKeyDown}
                        className="min-w-[140px] flex-1 text-sm outline-none"
                        placeholder={TAG_INPUT_PLACEHOLDER}
                    />
                </div>
                <div className="mt-3 flex justify-end">
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleSubmit}
                        disabled={savingStatus || (selectedStatus === record.status && tags.length === 0)}
                        sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            backgroundColor: "#7C3AED",
                            "&:hover": { backgroundColor: "#6D28D9" },
                        }}
                    >
                        {savingStatus ? "Submitting..." : "Submit"}
                    </Button>
                </div>
            </div>
            <EnquiryHrComments comments={record.comments} />
        </section>
    );
};
